import type { SettingValue } from '@jyfk/settings/types';
import { htmlLang, LANGS, T, type Lang, type Trilingual } from './i18n.ts';

/**
 * 外观：从设置值 → `<html>` 上的 data-* 属性。
 *
 * ════════════════════════════════════════════════════════
 * 这是整套样式系统的唯一入口。
 * ════════════════════════════════════════════════════════
 * `theme.css` 和 `ui.css` 里所有规则都挂在 `[data-xxx=yyy]` 上，
 * 所以「换主题」不是重新渲染组件，而是改一个属性 ——
 * 130 KB 的 CSS 自己会跟上。
 *
 * ── 值存在哪里 ──────────────────────────────────────────
 * **服务器是权威，localStorage 只是首屏缓存。**
 *
 * 全部 33 个维度和界面语言都在后端清单里有对应的配置项，
 * 所以它们跟着账号走：换台电脑登录，外观和语言是一样的。
 *
 * 配色、强调色、语言这三项曾经**只存在本机** —— 原型里它们是定制块
 * （主题卡片、色板、语言菜单），而生成脚本只收带 `k` 的普通控件。
 * 语言那一项还更糟：`user_profile` 上另有一个用户改不到的 `locale` 列，
 * 每次启动时把本机选的语言覆盖回简体中文 —— 也就是说
 * **语言切换活不过一次刷新**，而整条链路没有一处报错。
 *
 * 现在三项都进了清单（`ty: 'custom'` + `kind: 'enum'` ——
 * 界面仍然由 LookExtras 画，因为主题卡片要用真色值预览），
 * 那一列也删了（迁移 0006）。
 *
 * 为什么还要一份 localStorage 缓存：首屏必须在 JS 跑之前就把属性写好
 * （见 index.html 里那段内联脚本），否则会看到一次
 * 「默认黑曜岩 → 用户选的宣纸」的闪白，深色转浅色尤其刺眼。
 * 等 `/api/settings` 返回再改就来不及了。
 *
 * 缓存**只从服务器确认过的值写**，不写预览中的值 ——
 * 否则「改了没保存就刷新」会先按预览值画一遍，再被服务器的旧值改回去，
 * 用户看到的是莫名其妙跳一下。
 */

/* ══════════════════════ 维度清单 ══════════════════════ */

/**
 * 全部维度。每一个在后端清单里都有同名配置项，所以都跟着账号走。
 *
 * 绝大多数是 CSS 驱动的（`[data-x=y]` 有规则），
 * `numfmt` / `clock` 只有 JS 读 —— 但一样写到 `<html>` 上，
 * 这样 `format.ts` 里的格式化函数不必把它们一路当参数传下去。
 *
 * **`iconstyle` 也在这份清单里，但 CSS 里没有它的规则** —— 它是 JS 选图标集（见 components/Icon.tsx）。
 * `cursor`（自绘光标）的规则在 ui.css 末尾（2026-10-29 做的）。
 *
 * ── 顺序有意义 ──
 * `theme` 和 `accent` 排在最前面，和 `index.html` 里那份手抄的 DIMS 一致
 * （有测试盯着两边一字不差，含顺序）。它们原来在一个单独的 LOCAL_DIMS
 * 数组里排在前面，进清单之后并进来，位置保持不变 —— 这样 index.html 不用动。
 *
 * **界面语言不在这里。** 它虽然也是清单里的设置项（键名 `lang`），
 * 但它写到 `<html lang>` 这个标准属性上，不是 `data-*` 维度 ——
 * 见 Look.lang 和 applyLook。
 */
export const SERVER_DIMS = [
  'theme', 'accent',
  'iconstyle', 'iconweight', 'feel', 'cursor', 'btnfill', 'textc',
  'updown', 'updownmark', 'density', 'font', 'layout', 'radius',
  'motion', 'speed', 'hoverfx', 'glow',
  'lh', 'tbl', 'fontface', 'numface', 'tabnum', 'maxw', 'sidew',
  'cardstyle', 'chip', 'display', 'caps', 'shadow', 'focus',
  'numfmt', 'clock'
] as const;

export type Dim = (typeof SERVER_DIMS)[number];

/**
 * 写到 `<html>` 上的全部属性名。
 * **index.html 里那段内联脚本必须有同样一份**（有测试盯着，含顺序）。
 *
 * 这里原来是 `[...LOCAL_DIMS, ...SERVER_DIMS]`：配色和强调色曾经只存本机。
 * 现在它们也在清单里了，两份就并成了一份。
 */
export const HTML_DIMS: readonly Dim[] = SERVER_DIMS;

/* 「控件能用但效果还没接上」的项（比如 cursor）在 lib/key-notes.ts 里
   统一登记，界面上如实标出来 —— 那份清单不只管外观，所以不放这里 */

/* ══════════════════════ 主题与手感 ══════════════════════ */

/**
 * 每套配色自带的手感族。
 *
 * **index.html 里有一份副本**（首屏内联脚本要用），
 * 有一条测试盯着两边一致 —— 手抄两份必然漂移，
 * 而漂移的表现是「首屏那一下手感不对，然后忽然跳一下」。
 */
export const FEEL_OF_THEME: Readonly<Record<string, string>> = {
  obsidian: 'reveal',
  ember: 'ember',
  cellini: 'reveal',
  nocturne: 'glass',
  graphite: 'terminal',
  paper: 'ink',
  ivory: 'reveal',
  institutional: 'terminal'
};

export const FEEL_IDS = ['reveal', 'ink', 'ember', 'glass', 'terminal', 'off'] as const;

/** 八套配色。名字和说明照原型，卡片预览用真配色渲染 */
export const THEMES: ReadonlyArray<{
  id: string;
  name: Trilingual;
  note: Trilingual;
  tag?: Trilingual;
}> = [
  {
    id: 'obsidian',
    name: T('黑曜岩', '黑曜岩', 'Obsidian Vault'),
    note: T('中性材质黑，零强调色', '中性材質黑，零強調色', 'Neutral black, no accent'),
    tag: T('默认', '預設', 'Default')
  },
  {
    id: 'ember',
    name: T('爱马仕之烬', '愛馬仕之燼', 'Hermès Ember'),
    note: T('暖色温，橙作唯一强调。夜里疲劳度最低', '暖色溫，橙作唯一強調。夜間疲勞度最低', 'Warm, single orange accent')
  },
  {
    id: 'cellini',
    name: T('蚝式绿', '蠔式綠', 'Rolex Cellini'),
    note: T('深绿加古金按钮', '深綠加古金按鈕', 'Deep green with antique gold')
  },
  {
    id: 'nocturne',
    name: T('蒂芙尼夜', '蒂芙尼夜', 'Tiffany Nocturne'),
    note: T('蒂芙尼蓝兼任信息色', '蒂芙尼藍兼任資訊色', 'Tiffany blue doubles as the info colour')
  },
  {
    id: 'graphite',
    name: T('石墨终端', '石墨終端', 'Graphite Terminal'),
    note: T('工程派，信息密度最高', '工程派，資訊密度最高', 'Engineering feel, densest')
  },
  {
    id: 'paper',
    name: T('宣纸账簿', '宣紙賬簿', 'Paper Ledger'),
    note: T('暖纸底加墨色。白天最耐看', '暖紙底加墨色。日間最耐看', 'Warm paper and ink')
  },
  {
    id: 'ivory',
    name: T('象牙工坊', '象牙工坊', 'Ivory Atelier'),
    note: T('卡地亚红只在紧急时出现一次', '卡地亞紅只在緊急時出現一次', 'Cartier red appears once, for urgent only')
  },
  {
    id: 'institutional',
    name: T('机构灰', '機構灰', 'Institutional Grey'),
    note: T('数据派，灰阶来自实测', '數據派，灰階來自實測', 'Data-first greys, measured')
  }
];

/** 强调色。`swatch` 为 null 表示跟随主题自己的强调色 */
export const ACCENTS: ReadonlyArray<{ id: string; name: Trilingual; swatch: string | null }> = [
  { id: 'theme', name: T('跟随主题', '跟隨主題', 'Follow theme'), swatch: null },
  { id: 'blue', name: T('钢蓝', '鋼藍', 'Steel blue'), swatch: '#4C8DF6' },
  { id: 'teal', name: T('青瓷', '青瓷', 'Teal'), swatch: '#2BB3A3' },
  { id: 'green', name: T('松绿', '松綠', 'Pine'), swatch: '#2FA96B' },
  { id: 'gold', name: T('古金', '古金', 'Antique gold'), swatch: '#B59E5F' },
  { id: 'amber', name: T('琥珀', '琥珀', 'Amber'), swatch: '#D99A2B' },
  { id: 'red', name: T('朱砂', '朱砂', 'Vermilion'), swatch: '#DD4F57' },
  { id: 'violet', name: T('紫藤', '紫藤', 'Wisteria'), swatch: '#8B7BF0' }
];

/* ══════════════════════ 本机缓存 ══════════════════════ */

/** localStorage 的键。和 index.html 里那段内联脚本必须一致 */
export const LOOK_KEY = 'jyfk.look';

/**
 * 缓存格式的版本号。
 *
 * 改过默认值之后，存在浏览器里的旧值会盖掉新默认值 ——
 * 表现是「我改了默认样式，用户刷新却什么都没变」。
 * 版本变了就整份丢掉重新从服务器取（反正它只是缓存）。
 */
export const LOOK_VER = 2;

export interface Look {
  lang: Lang;
  /** 维度 → 取值。缺的那些走 DEFAULT_LOOK */
  dims: Record<string, string>;
}

/**
 * 首屏兜底。
 *
 * 这些值和后端清单里的 default 必须一致 —— 有测试盯着。
 * 不一致的表现很隐蔽：第一次打开是 A，服务器返回之后变成 B。
 */
export const DEFAULT_LOOK: Look = {
  lang: 'zh-CN',
  dims: {
    theme: 'obsidian',
    /* theme = 跟配色自带的强调色。CSS 里刻意没有 [data-accent="theme"] 规则 */
    accent: 'theme',
    iconstyle: 'solar',
    iconweight: 'normal',
    /* auto = 跟当前配色自带的那一档（见 FEEL_OF_THEME） */
    feel: 'auto',
    /* 系统箭头。盯盘工具劫持光标是大忌 */
    cursor: 'system',
    btnfill: 'theme',
    /* standard = APCA Lc 45 起步（实测四套深色主题原来只有 32–42） */
    textc: 'standard',
    updown: 'green-up',
    updownmark: 'off',
    density: 'standard',
    font: 'm',
    layout: 'editorial',
    radius: 'sharp',
    motion: 'full',
    speed: 'normal',
    hoverfx: 'on',
    glow: 'on',
    lh: 'normal',
    tbl: 'normal',
    fontface: 'sans',
    numface: 'mono',
    tabnum: 'on',
    maxw: 'normal',
    sidew: 'normal',
    cardstyle: 'rule',
    chip: 'outline',
    display: 'serif',
    caps: 'on',
    shadow: 'none',
    focus: 'normal',
    numfmt: 'km',
    clock: '24h'
  }
};

/** 解析 `feel=auto` 成具体档位 */
export function resolveFeel(dims: Readonly<Record<string, string>>): string {
  const f = dims['feel'] ?? 'auto';
  const byTheme = FEEL_OF_THEME[dims['theme'] ?? 'obsidian'] ?? 'reveal';
  /* 旧缓存里可能存过已经不存在的值，兜回按主题算 */
  if (f === 'auto' || !(FEEL_IDS as readonly string[]).includes(f)) return byTheme;
  return f;
}

/**
 * 认识的语言。**从 LANGS 派生，不手抄** ——
 * 手抄一份的结果是加了第四种语言时这里漏掉，
 * 而表现是「语言菜单里有，选了不生效」。
 *
 * 清单里 `lang` 那一项的 options 是第三份（它要给后端校验用），
 * 有一条测试盯着它和 LANGS 一致。
 */
const LANG_OK = new Set<string>(LANGS.map(([id]) => id));

/** 读首屏缓存。任何异常都退回默认，绝不因此白屏 */
export function loadLook(): Look {
  let raw: unknown = null;
  try {
    raw = JSON.parse(localStorage.getItem(LOOK_KEY) ?? 'null');
  } catch {
    raw = null;
  }
  if (raw === null || typeof raw !== 'object') return DEFAULT_LOOK;
  const o = raw as { v?: unknown; lang?: unknown; dims?: unknown };
  if (o.v !== LOOK_VER) return DEFAULT_LOOK;

  const dims: Record<string, string> = { ...DEFAULT_LOOK.dims };
  if (o.dims !== null && typeof o.dims === 'object') {
    for (const [k, v] of Object.entries(o.dims as Record<string, unknown>)) {
      /* 只认识清单里的维度。多出来的键一律丢掉 ——
         不然一个被我们删掉的维度会永远留在别人的浏览器里 */
      if ((HTML_DIMS as readonly string[]).includes(k) && typeof v === 'string') dims[k] = v;
    }
  }
  return {
    lang: typeof o.lang === 'string' && LANG_OK.has(o.lang) ? (o.lang as Lang) : DEFAULT_LOOK.lang,
    dims
  };
}

export function saveLook(look: Look): void {
  try {
    localStorage.setItem(LOOK_KEY, JSON.stringify({ v: LOOK_VER, lang: look.lang, dims: look.dims }));
  } catch {
    /* 隐身模式或 localStorage 满了。不该因此崩掉界面 ——
       只是这次的外观选择活不过刷新，那是浏览器的限制不是我们的 bug */
  }
}

/* ══════════════════════ 从设置值取外观 ══════════════════════ */

/**
 * 服务器设置 → 完整外观。**这是唯一的来源。**
 *
 * 以前还要传第二个参数，因为配色、强调色、语言只存在本机；
 * 现在它们都是设置项，所以一份设置就能算出全部外观。
 * 少一个参数的实际意义是：不可能再出现「一半从服务器取、一半从别处取」
 * 那种半新半旧的外观了。
 *
 * 服务器上的值可能是我们不认识的（老版本存过、或者被手工改过），
 * 维度这边不做校验 —— 写到 `<html>` 上没有对应 CSS 规则的值，
 * 表现就是「这个维度用基准样式」，不会出错。真正的校验在服务端。
 *
 * **语言例外，要校验。** 它写进 `<html lang>` 并决定所有文案走哪本词典，
 * 一个不认识的值会让 `tx()` 全部落空 —— 那不是「样式退回基准」，
 * 那是整个界面变成空白标签。
 */
export function lookFromSettings(settings: Readonly<Record<string, SettingValue>>): Look {
  const dims: Record<string, string> = { ...DEFAULT_LOOK.dims };
  for (const d of SERVER_DIMS) {
    const v = settings[d];
    if (typeof v === 'string' && v.length > 0) dims[d] = v;
  }
  const rawLang = settings['lang'];
  const lang: Lang = typeof rawLang === 'string' && LANG_OK.has(rawLang)
    ? (rawLang as Lang)
    : DEFAULT_LOOK.lang;
  return { lang, dims };
}

/**
 * 外观 → 设置值。`lookFromSettings` 的反方向，**只给游客模式用**。
 *
 * 游客没有服务器上的设置，他的外观和语言只存在这台电脑上（首屏缓存）。
 * 设置页读的是「设置值」，所以要把本机的外观折回设置值 ——
 * 不折的话外观页会显示默认的「黑曜岩 + 简体中文」，
 * 而整个界面其实已经是他选的英文，两边对不上。
 */
export function settingsFromLook(look: Look): Record<string, string> {
  const out: Record<string, string> = {};
  for (const d of SERVER_DIMS) {
    const v = look.dims[d];
    if (typeof v === 'string' && v.length > 0) out[d] = v;
  }
  out['lang'] = look.lang;
  return out;
}

/**
 * 把外观写到 `<html>` 上。
 *
 * `data-feel` **永远写解析后的具体档位，不写 auto** ——
 * 否则 theme.css 要给 8 套配色各抄一份手感配方。
 * 用户选的原始值仍然存在设置里，界面上照样显示「跟随配色」。
 */
export function applyLook(look: Look): void {
  const r = document.documentElement;
  for (const d of HTML_DIMS) {
    const v = look.dims[d];
    if (v !== undefined && v.length > 0) r.dataset[d] = v;
  }
  r.dataset['feel'] = resolveFeel(look.dims);
  r.setAttribute('lang', htmlLang(look.lang));
}
